import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, BehaviorSubject, catchError, filter, switchMap, take, throwError } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { Router } from '@angular/router';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(
    private authService: AuthService,
    private router: Router
  ) {}

  private isSelfMembershipRead(request: HttpRequest<any>): boolean {
    return request.method === 'GET'
      && /\/users\/me\/organizations(?:\?|$)/.test(request.url);
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Skip interceptor for auth endpoints, public endpoints, and S3 presigned URLs
    if (req.url.includes('/auth/') || req.url.includes('/health') || req.url.includes('/config')
        || req.url.includes('.s3.') || req.url.includes('s3.amazonaws.com')) {
      return next.handle(req);
    }

    // Get the access token
    const accessToken = this.authService.getAccessToken();
    
    if (accessToken) {
      // Build headers with auth token and organization context
      const headers: any = {
        Authorization: `Bearer ${accessToken}`
      };
      
      // Add organization header if available
      const currentOrgId = this.authService.getCurrentOrganizationId();
      // This self-scoped endpoint must remain reachable after a cached org is
      // revoked, so callers can fetch their current memberships.
      if (currentOrgId && !this.isSelfMembershipRead(req)) {
        headers['X-Organization-Id'] = currentOrgId;
      }
      
      const authReq = req.clone({
        setHeaders: headers
      });

      return next.handle(authReq).pipe(
        catchError(error => {
          if (error instanceof HttpErrorResponse && error.status === 401) {
            return this.handle401Error(req, next);
          }
          
          return throwError(() => error);
        })
      );
    }

    // If no token, just pass the request through
    return next.handle(req);
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const refreshToken = localStorage.getItem('refreshToken');
      
      if (refreshToken) {
        return this.authService.refreshToken().pipe(
          switchMap((response: any) => {
            this.isRefreshing = false;
            
            if (response.success && response.data?.accessToken) {
              this.refreshTokenSubject.next(response.data.accessToken);
              // Retry the original request with the new token
              const retryHeaders: any = {
                Authorization: `Bearer ${response.data.accessToken}`
              };
              
              const currentOrgId = this.authService.getCurrentOrganizationId();
              if (currentOrgId && !this.isSelfMembershipRead(request)) {
                retryHeaders['X-Organization-Id'] = currentOrgId;
              }
              
              const retryReq = request.clone({
                setHeaders: retryHeaders
              });
              return next.handle(retryReq);
            }
            
            // If refresh failed, logout and redirect
            this.handleAuthError();
            return throwError(() => new Error('Token refresh failed'));
          }),
          catchError((err) => {
            this.isRefreshing = false;
            this.handleAuthError();
            return throwError(() => err);
          })
        );
      } else {
        // No refresh token available
        this.handleAuthError();
        return throwError(() => new Error('No refresh token available'));
      }
    } else {
      // Wait for refresh to complete
      return this.refreshTokenSubject.pipe(
        filter(token => token != null),
        take(1),
        switchMap(token => {
          const retryHeaders: any = {
            Authorization: `Bearer ${token}`
          };
          
          const currentOrgId = this.authService.getCurrentOrganizationId();
          if (currentOrgId && !this.isSelfMembershipRead(request)) {
            retryHeaders['X-Organization-Id'] = currentOrgId;
          }
          
          const retryReq = request.clone({
            setHeaders: retryHeaders
          });
          return next.handle(retryReq);
        })
      );
    }
  }

  private handleAuthError(): void {
    // Store the current URL before logout
    const currentUrl = this.router.url;
    
    // Perform logout which will clear all data
    this.authService.logout();
    
    // Small delay to ensure logout completes
    setTimeout(() => {
      // Navigate only if we're not already on the login page
      if (!this.router.url.includes('/auth/login')) {
        this.router.navigate(['/auth/login'], {
          queryParams: { 
            message: 'Your session has expired. Please login again.',
            returnUrl: currentUrl !== '/dashboard' ? currentUrl : undefined
          }
        });
      }
    }, 100);
  }
}
